import React, { useState, useEffect } from "react";
import { Box, Card, IconButton, Icon, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, CircularProgress, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { get, post, postNoResponse } from "utils/WebService";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import { maskPhoneWithDdd } from "utils/Functions";
import { TIPO_PERFIL, temTipoPerfil } from "utils/Perfis";
import Contratos from "./Contratos";
import Historico from "./Historico";
import Acordos from "./Acordos";
import { ESCURO } from "assets/coresTema";

export default function Honorarios({ formRef, recordId, isViewOnly, pendingContratos = [], setPendingContratos, changeTab, onDataUpdated, refreshKey, preloadedDevedor, preloadedContratos, preloadedAcordos, preloadedHistoricos, onNavigateToDevedor }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [fichas, setFichas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFichaId, setActiveFichaId] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };
    const { userPerfils, userFunctionalities } = useUser();
    
    // Roles & Permissões
    const canCreate = userFunctionalities && userFunctionalities.includes("c050_C");
    const canEdit = userFunctionalities && userFunctionalities.includes("c050_U");
    const isAdminOrGestor = temTipoPerfil(userPerfils, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE, TIPO_PERFIL.GESTOR);
    const isSupervisorOrCoordenador = temTipoPerfil(userPerfils, TIPO_PERFIL.SUPERVISOR, TIPO_PERFIL.COORDENADOR);
    const isOperador = !canCreate && !canEdit && !isAdminOrGestor && !isSupervisorOrCoordenador;

    // Form States
    const [editForm, setEditForm] = useState({ carteiraId: "", statusId: "", usuarioId: "", marcacoesIds: [] });
    const [carteirasOptions, setCarteirasOptions] = useState([]);
    const [isCarteiraLocked, setIsCarteiraLocked] = useState(false);
    const [statusOptions, setStatusOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [marcacoesOptions, setMarcacoesOptions] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    
    // Add Modal States (Honorário Avulso)
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [codigoTerceiroDiferente, setCodigoTerceiroDiferente] = useState(false);
    const [addForm, setAddForm] = useState({
        carteiraId: "",
        statusId: "",
        usuarioId: "",
        marcacoesIds: [],
        titulo: "Honorários Advocatícios",
        valorOriginal: "",
        vencimento: new Date().toISOString().split("T")[0],
        tipoHonorario: 5,
        descricao: "",
        isTerceiro: false,
        nomeTerceiro: "",
        documentoTerceiro: "",
        codigoClienteTerceiro: "",
        telefoneTerceiro: "",
        emailTerceiro: ""
    });

    const tipoHonorarioOptions = [
        { id: 1, nome: "Sucumbenciais" },
        { id: 2, nome: "Contratuais" },
        { id: 3, nome: "Iniciais" },
        { id: 4, nome: "Finais" },
        { id: 5, nome: "Outros" },
    ];

    // Snackbar States
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const loadFichas = () => {
        if (preloadedDevedor !== undefined) {
            if (preloadedDevedor && preloadedDevedor.fichas) {
                const honorarios = preloadedDevedor.fichas.filter(f => f.isHonorario === true || f.cbIsHonorario === true);
                setFichas(honorarios);
            } else {
                setFichas([]);
            }
            setLoading(false);
            return;
        }

        setLoading(true);
        get(`/devedor/by-id/${recordId}`)
            .then(res => {
                if (res && res.fichas) {
                    const honorarios = res.fichas.filter(f => f.isHonorario === true || f.cbIsHonorario === true);
                    setFichas(honorarios);
                }
            })
            .catch(err => console.error("Erro ao carregar fichas de honorários", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (recordId || preloadedDevedor) {
            loadFichas();
        }
    }, [recordId, refreshKey, preloadedDevedor]);

    const activeFicha = activeFichaId ? fichas.find(f => f.id === activeFichaId || f.rawId === activeFichaId) : null;

    useEffect(() => {
        if (activeFicha) {
            setEditForm({
                carteiraId: activeFicha.idCarteira || activeFicha.carteiraId || "",
                statusId: activeFicha.idStatusFicha || activeFicha.statusId || "",
                usuarioId: activeFicha.idUsuarioDono || activeFicha.usuarioId || "",
                marcacoesIds: activeFicha.marcacoesIds || (activeFicha.tbMarcacoesEspeciais ? activeFicha.tbMarcacoesEspeciais.map(m => m.cvId || m.id) : [])
            });
            
            if (carteirasOptions.length === 0) {
                get("/carteira/usuario-contexto").then(res => {
                    let list = res?.carteiras || [];
                    const filtered = list.filter(c => 
                        c.nome?.trim().toLowerCase() !== "processos" && 
                        !c.nome?.toLowerCase().includes("processo")
                    );
                    setCarteirasOptions(filtered);
                }).catch(() => {
                    get("/carteira/list-all").then(res => {
                        let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                        const filtered = list.filter(c => 
                            c.nome?.trim().toLowerCase() !== "processos" && 
                            !c.nome?.toLowerCase().includes("processo")
                        );
                        setCarteirasOptions(filtered);
                    }).catch(() => {});
                });
            }
            if (statusOptions.length === 0) {
                get("/status-ficha/list-all").then(res => {
                    let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                    setStatusOptions(list);
                }).catch(() => {});
            }
            if (usuariosOptions.length === 0) {
                get("/users/list-all-operators").then(res => setUsuariosOptions(res?.list || res?.data || (Array.isArray(res) ? res : []))).catch(() => {});
            }
            if (marcacoesOptions.length === 0) {
                get("/marcacoes").then(res => {
                    let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                    setMarcacoesOptions(list.filter(m => m.cvAlvo === 1 || m.cvAlvo === 3));
                }).catch(() => {});
            }
        }
    }, [activeFichaId, fichas]);

    const handleBackToGrid = () => {
        setActiveFichaId(null);
        setTabValue(0);
    };

    const handleOpenDetail = (ficha) => {
        setActiveFichaId(ficha.id || ficha.rawId);
        setTabValue(0);
    };

    const handleSaveEdit = () => {
        if (!activeFicha) return;
        setIsSaving(true);
        postNoResponse(`/devedor/carteira/update/${activeFicha.id || activeFicha.rawId}`, editForm)
            .then(() => {
                setSbMessage("Ficha de honorários atualizada com sucesso!");
                setSuccessSB(true);
                if (recordId) {
                    get(`/devedor/by-id/${recordId}`).then(res => {
                        if (res && res.fichas) {
                            const honorarios = res.fichas.filter(f => f.isHonorario === true || f.cbIsHonorario === true);
                            setFichas(honorarios);
                        }
                    }).catch(() => loadFichas());
                } else {
                    loadFichas();
                }
                if (onDataUpdated) onDataUpdated();
            })
            .catch(() => {
                setSbMessage("Erro ao atualizar ficha de honorários.");
                setErrorSB(true);
            })
            .finally(() => setIsSaving(false));
    };

    const handleOpenAdd = () => {
        setCodigoTerceiroDiferente(false);
        setAddForm({
            carteiraId: "",
            statusId: "",
            usuarioId: "",
            marcacoesIds: [],
            titulo: "Outros",
            valorOriginal: "",
            vencimento: new Date().toISOString().split("T")[0],
            tipoHonorario: 5,
            descricao: "",
            isTerceiro: false,
            nomeTerceiro: "",
            documentoTerceiro: "",
            codigoClienteTerceiro: "",
            telefoneTerceiro: "",
            emailTerceiro: ""
        });
        
        get("/carteira/usuario-contexto").then(res => {
            if (res && Array.isArray(res.carteiras)) {
                const filtered = res.carteiras.filter(c => 
                    c.nome?.trim().toLowerCase() !== "processos" && 
                    !c.nome?.toLowerCase().includes("processo")
                );
                setCarteirasOptions(filtered);
                setIsCarteiraLocked(!!res.isLocked);
                if (res.isLocked && res.defaultCarteiraId) {
                    setAddForm(prev => ({ ...prev, carteiraId: res.defaultCarteiraId }));
                } else if (res.isLocked && filtered.length === 1) {
                    setAddForm(prev => ({ ...prev, carteiraId: filtered[0].id }));
                } else {
                    setAddForm(prev => ({ ...prev, carteiraId: "" }));
                }
            }
        }).catch(() => {
            get("/carteira/list-all").then(res => {
                let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                const filtered = list.filter(c => 
                    c.nome?.trim().toLowerCase() !== "processos" && 
                    !c.nome?.toLowerCase().includes("processo")
                );
                setCarteirasOptions(filtered);
            }).catch(() => {});
        });
        if (statusOptions.length === 0) {
            get("/status-ficha/list-all").then(res => {
                let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                setStatusOptions(list);
            }).catch(() => {});
        }
        if (usuariosOptions.length === 0) {
            get("/users/list-all-operators").then(res => setUsuariosOptions(res?.list || res?.data || (Array.isArray(res) ? res : []))).catch(() => {});
        }
        if (marcacoesOptions.length === 0) {
            get("/marcacoes").then(res => {
                let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                setMarcacoesOptions(list.filter(m => m.cvAlvo === 1 || m.cvAlvo === 3));
            }).catch(() => {});
        }

        setAddModalOpen(true);
    };

    const handleSaveAdd = () => {
        if (!addForm.carteiraId) {
            setSbMessage("Por favor, selecione uma Carteira.");
            setErrorSB(true);
            return;
        }
        if (!addForm.valorOriginal || parseFloat(addForm.valorOriginal) <= 0) {
            setSbMessage("Por favor, informe um valor válido para o débito de honorários.");
            setErrorSB(true);
            return;
        }
        if (addForm.isTerceiro) {
            if (!addForm.nomeTerceiro || !addForm.nomeTerceiro.trim()) {
                setSbMessage("Por favor, informe o Nome Completo do Terceiro / Embargante.");
                setErrorSB(true);
                return;
            }
            const codTerceiro = (addForm.codigoClienteTerceiro || "").trim() || (addForm.documentoTerceiro || "").trim();
            if (!codTerceiro) {
                setSbMessage("O Código do Cliente do Terceiro não pode ser vazio.");
                setErrorSB(true);
                return;
            }
        }

        setIsSaving(true);
        post("/devedor/honorario-avulso/save", {
            idDevedor: recordId,
            carteiraId: addForm.carteiraId,
            statusId: addForm.statusId,
            usuarioId: addForm.usuarioId,
            marcacoesIds: addForm.marcacoesIds,
            titulo: addForm.titulo || "Honorários Advocatícios",
            valorOriginal: addForm.valorOriginal,
            vencimento: addForm.vencimento,
            tipoHonorario: addForm.tipoHonorario || 5,
            descricao: addForm.descricao,
            isTerceiro: !!addForm.isTerceiro,
            nomeTerceiro: addForm.nomeTerceiro,
            documentoTerceiro: addForm.documentoTerceiro,
            codigoClienteTerceiro: (addForm.codigoClienteTerceiro || "").trim() || (addForm.documentoTerceiro || "").trim(),
            telefoneTerceiro: addForm.telefoneTerceiro ? addForm.telefoneTerceiro.replace(/\D/g, '') : null,
            emailTerceiro: addForm.emailTerceiro
        })
        .then((res) => {
            const idCriado = res?.data?.idDevedorCriado || res?.idDevedorCriado;
            setSbMessage(addForm.isTerceiro ? "Ficha de honorários do terceiro cadastrada com sucesso!" : "Honorário avulso cadastrado com sucesso!");
            setSuccessSB(true);
            setAddModalOpen(false);
            if (onDataUpdated) onDataUpdated();

            if (addForm.isTerceiro && idCriado && onNavigateToDevedor) {
                onNavigateToDevedor(idCriado);
            } else if (recordId) {
                get(`/devedor/by-id/${recordId}`).then(resDev => {
                    if (resDev && resDev.fichas) {
                        const honorarios = resDev.fichas.filter(f => f.isHonorario === true || f.cbIsHonorario === true);
                        setFichas(honorarios);
                    }
                }).catch(() => loadFichas());
            } else {
                loadFichas();
            }
        })
        .catch((err) => {
            console.error("Erro ao cadastrar honorário", err);
            setSbMessage("Erro ao cadastrar honorário avulso.");
            setErrorSB(true);
        })
        .finally(() => setIsSaving(false));
    };

    const handleDelete = (id) => {
        if (window.confirm("Deseja realmente excluir esta ficha de honorários? Todos os contratos atrelados a ela também serão apagados. O acordo formalizado ficará sem os honorários.")) {
            postNoResponse(`/devedor/carteira/delete/${id}`, {})
                .then(() => {
                    setSbMessage("Ficha excluída com sucesso!");
                    setSuccessSB(true);
                    if (onDataUpdated) onDataUpdated();
                    else loadFichas();
                })
                .catch(() => {
                    setSbMessage("Erro ao excluir ficha.");
                    setErrorSB(true);
                });
        }
    };

    const columns = [
        { 
            header: "ID", 
            accessorKey: "rawId", 
            align: "center", 
            width: "10%" 
        },
        { 
            header: "Carteira", 
            accessorKey: "nomeCarteira", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeCarteira || "Não informada"}
                </MDTypography>
            )
        },
        { 
            header: "Status", 
            accessorKey: "nomeStatusFicha", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeStatusFicha || "Sem status"}
                </MDTypography>
            )
        },
        { 
            header: "Propriedade", 
            accessorKey: "nomeUsuarioDono", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeUsuarioDono || "Sem responsável"}
                </MDTypography>
            )
        },
        {
            header: "Ações",
            accessorKey: "actions",
            align: "center",
            cell: ({ row }) => (
                <MDBox display="flex" justifyContent="center">
                    <IconButton onClick={() => handleOpenDetail(row.original)} color="info" size="small" title="Visualizar">
                        <Icon>visibility</Icon>
                    </IconButton>
                    <IconButton onClick={() => handleOpenDetail(row.original)} color="warning" size="small" title="Editar">
                        <Icon>edit</Icon>
                    </IconButton>
                    {(!isOperador && !isViewOnly) && (
                        <IconButton onClick={() => handleDelete(row.original.id)} color="error" size="small" title="Excluir">
                            <Icon>delete</Icon>
                        </IconButton>
                    )}
                </MDBox>
            )
        }
    ];

    return (
        <MDBox mt={2}>
            {activeFichaId ? (
                <Card id="honorarios-ficha-detalhe" sx={{ boxShadow: "none" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} flexWrap="wrap" gap={1}>
                        <MDButton 
                            variant="outlined" 
                            color="secondary" 
                            size="small"
                            onClick={handleBackToGrid}
                            sx={{
                                color: darkMode ? "#ffffff !important" : undefined,
                                borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                            }}
                        >
                            <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Voltar
                        </MDButton>

                        <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <MDButton 
                                variant="contained" 
                                color="info" 
                                size="small" 
                                onClick={handleOpenAdd}
                                sx={{
                                    backgroundColor: darkMode ? "#007bff !important" : undefined,
                                    color: "#ffffff !important",
                                    fontWeight: "bold",
                                    boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                    "&:hover": {
                                        backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                    }
                                }}
                            >
                                <Icon sx={{ mr: 0.5 }}>add</Icon> Adicionar Honorário Avulso
                            </MDButton>
                        </MDBox>
                    </MDBox>
                    
                    <MDBox sx={{ width: '100%', px: 2, mb: 4 }}>
                        <MDBox sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={handleChangeTab} aria-label="honorarios-tabs">
                                <Tab label="Dados" value={0} sx={{ fontSize: 12 }} />
                                <Tab label="Contratos" value={1} sx={{ fontSize: 12 }} />
                                <Tab label="Histórico" value={2} sx={{ fontSize: 12 }} />
                                <Tab label="Acordos" value={3} sx={{ fontSize: 12 }} />
                            </Tabs>
                        </MDBox>

                        <MDBox mt={3}>
                            <div role="tabpanel" hidden={tabValue !== 0}>
                                {tabValue === 0 && activeFicha && (
                                    <Card sx={{ p: 2, boxShadow: "none", bgcolor: darkMode ? ESCURO.cartao : "#fafafa", borderRadius: "12px", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0"}` }}>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                                            <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                                Dados da Ficha de Honorários #{activeFicha.rawId || activeFicha.id}
                                            </MDTypography>
                                        </MDBox>

                                        <Grid container spacing={3} alignItems="flex-start">
                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Carteira
                                                    </MDTypography>
                                                    <MDSelect
                                                        isMulti={false}
                                                        getOptionValue={(option) => option.id}
                                                        getOptionLabel={(option) => option.nome}
                                                        placeholder="Selecione..."
                                                        value={carteirasOptions.find(c => String(c.id) === String(editForm.carteiraId)) || (activeFicha.nomeCarteira ? { id: activeFicha.idCarteira, nome: activeFicha.nomeCarteira } : null)}
                                                        options={carteirasOptions}
                                                        onChange={(val) => setEditForm({ ...editForm, carteiraId: val ? val.id : "" })}
                                                        isDisabled={!isAdminOrGestor}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Status da Ficha
                                                    </MDTypography>
                                                    <MDSelect
                                                        isMulti={false}
                                                        getOptionValue={(option) => option.id}
                                                        getOptionLabel={(option) => option.nome || option.ccNome}
                                                        placeholder="Selecione..."
                                                        value={statusOptions.find(s => String(s.id) === String(editForm.statusId)) || (activeFicha.nomeStatusFicha ? { id: activeFicha.idStatusFicha, nome: activeFicha.nomeStatusFicha } : null)}
                                                        options={statusOptions}
                                                        onChange={(val) => setEditForm({ ...editForm, statusId: val ? val.id : "" })}
                                                        isDisabled={false}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Responsável (Propriedade)
                                                    </MDTypography>
                                                    <MDSelect
                                                        isMulti={false}
                                                        getOptionValue={(option) => option.id}
                                                        getOptionLabel={(option) => option.nome}
                                                        placeholder="Selecione..."
                                                        value={usuariosOptions.find(u => String(u.id) === String(editForm.usuarioId)) || (activeFicha.nomeUsuarioDono ? { id: activeFicha.idUsuarioDono, nome: activeFicha.nomeUsuarioDono } : null)}
                                                        options={usuariosOptions}
                                                        onChange={(val) => setEditForm({ ...editForm, usuarioId: val ? val.id : "" })}
                                                        isDisabled={isOperador || isViewOnly}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Marcação Especial
                                                    </MDTypography>
                                                    <MDSelect
                                                        isMulti={true}
                                                        getOptionValue={(option) => option.cvId}
                                                        getOptionLabel={(option) => option.ccNome}
                                                        placeholder="Selecione..."
                                                        value={marcacoesOptions.filter(m => editForm.marcacoesIds && editForm.marcacoesIds.includes(m.cvId))}
                                                        options={marcacoesOptions}
                                                        onChange={(selected) => setEditForm({ ...editForm, marcacoesIds: selected ? selected.map(s => s.cvId) : [] })}
                                                        isDisabled={isOperador || isViewOnly}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} display="flex" justifyContent="flex-end" mt={3} pt={2} sx={{ borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0" }}>
                                                <MDButton 
                                                    variant="contained" 
                                                    color="info" 
                                                    onClick={handleSaveEdit}
                                                    disabled={isSaving}
                                                    sx={{
                                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                                        color: "#ffffff !important",
                                                        fontWeight: "bold",
                                                        boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                                        "&:hover": {
                                                            backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                                        }
                                                    }}
                                                >
                                                    {isSaving ? <CircularProgress size={18} color="inherit" /> : "Salvar Dados"}
                                                </MDButton>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                )}
                            </div>
                            <div role="tabpanel" hidden={tabValue !== 1}>
                                {tabValue === 1 && (
                                    <Contratos 
                                        formRef={formRef} 
                                        recordId={recordId} 
                                        idDevedorCarteira={activeFichaId}
                                        isViewOnly={isViewOnly} 
                                        pendingContratos={pendingContratos} 
                                        setPendingContratos={setPendingContratos} 
                                        changeTab={handleChangeTab} 
                                        onDataUpdated={onDataUpdated} 
                                        isHonorariosTab={true}
                                        preloadedContratos={preloadedContratos}
                                        preloadedAcordos={preloadedAcordos}
                                        preloadedDevedor={preloadedDevedor}
                                    />
                                )}
                            </div>
                            <div role="tabpanel" hidden={tabValue !== 2}>
                                {tabValue === 2 && (
                                    <Historico 
                                        recordId={recordId} 
                                        isViewOnly={isViewOnly} 
                                        idDevedorCarteira={activeFichaId} 
                                        preloadedDevedor={preloadedDevedor}
                                        preloadedHistoricos={preloadedHistoricos}
                                        isHonorariosTab={true}
                                    />
                                )}
                            </div>
                            <div role="tabpanel" hidden={tabValue !== 3}>
                                {tabValue === 3 && (
                                    <Acordos 
                                        recordId={recordId} 
                                        isViewOnly={isViewOnly} 
                                        idDevedorCarteira={activeFichaId} 
                                        changeTab={handleChangeTab} 
                                        isHonorariosTab={true} 
                                        onDataUpdated={onDataUpdated} 
                                        preloadedAcordos={preloadedAcordos}
                                    />
                                )}
                            </div>
                        </MDBox>
                    </MDBox>
                </Card>
            ) : (
                <>
                    <MDBox display="flex" justifyContent="flex-end" mb={2}>
                        <MDButton 
                            variant="contained" 
                            color="info" 
                            size="small" 
                            onClick={handleOpenAdd}
                            sx={{
                                backgroundColor: darkMode ? "#007bff !important" : undefined,
                                color: "#ffffff !important",
                                fontWeight: "bold",
                                boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                "&:hover": {
                                    backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                }
                            }}
                        >
                            <Icon sx={{ mr: 0.5 }}>add</Icon> Adicionar Honorário Avulso
                        </MDButton>
                    </MDBox>

                    <DataTable
                        columnData={columns}
                        data={fichas}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                    />

                    {/* Fichas de Terceiros Vinculadas (Embargos de Terceiro) */}
                    {preloadedDevedor?.devedoresFilhos && preloadedDevedor.devedoresFilhos.length > 0 && (
                        <MDBox mt={4} mb={2}>
                            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                                <Icon sx={{ color: "#ffa000", fontSize: "22px !important" }}>account_tree</Icon>
                                <MDTypography variant="subtitle2" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                    Honorários Cobrados de Terceiros Vinculados (Embargos de Terceiro)
                                </MDTypography>
                            </MDBox>
                            <Card sx={{ p: 2, backgroundColor: darkMode ? ESCURO.painel : "#fff8e1", border: `1px solid ${darkMode ? "#ffa000" : "#ffe082"}` }}>
                                <DataTable
                                    columnData={[
                                        {
                                            header: "Ficha",
                                            accessorKey: "unencryptedId",
                                            align: "center",
                                            width: "10%",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" fontWeight="bold" color="text">
                                                    #{row.original.unencryptedId || row.original.id}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Nome do Terceiro",
                                            accessorKey: "nome",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDBox>
                                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                        {row.original.nome}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" display="block" color="text" sx={{ fontSize: "11px" }}>
                                                        CPF/CNPJ: {row.original.documento || "Não informado"}
                                                    </MDTypography>
                                                </MDBox>
                                            )
                                        },
                                        {
                                            header: "Carteira",
                                            accessorKey: "carteiraNome",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                                    {row.original.carteiraNome || "Honorários"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Status",
                                            accessorKey: "statusFichaNome",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                                    {row.original.statusFichaNome || "Sem status"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Responsável",
                                            accessorKey: "usuarioResponsavel",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                                    {row.original.usuarioResponsavel || "Sem responsável"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Valor Original",
                                            accessorKey: "valorTotal",
                                            align: "right",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" fontWeight="bold" color="text">
                                                    {row.original.valorTotal != null ? `R$ ${Number(row.original.valorTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "R$ 0,00"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Ação",
                                            accessorKey: "actions",
                                            align: "center",
                                            cell: ({ row }) => (
                                                <MDButton
                                                    variant="gradient"
                                                    color="warning"
                                                    size="small"
                                                    onClick={() => onNavigateToDevedor && onNavigateToDevedor(row.original.id)}
                                                    sx={{ py: 0.5, px: 1.5, fontSize: "11px", fontWeight: "bold" }}
                                                >
                                                    <Icon sx={{ mr: 0.5, fontSize: "16px !important" }}>open_in_new</Icon> Acessar Ficha
                                                </MDButton>
                                            )
                                        }
                                    ]}
                                    data={preloadedDevedor.devedoresFilhos}
                                    isSorted={false}
                                    entriesPerPage={false}
                                    showTotalEntries={false}
                                    noEndBorder
                                />
                            </Card>
                        </MDBox>
                    )}
                </>
            )}

            {/* Modal de Cadastrar Honorário Avulso */}
            <Dialog 
                open={addModalOpen} 
                onClose={() => !isSaving && setAddModalOpen(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? ESCURO.cartao : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>
                    Cadastrar Honorário Avulso (Ficha & Débito)
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : undefined }}>
                    <Grid container spacing={2} alignItems="flex-start">
                        {/* Seção 0: Cobrança de Terceiro */}
                        <Grid item xs={12}>
                            <MDBox 
                                display="flex" 
                                alignItems="center" 
                                justifyContent="space-between" 
                                p={1.5} 
                                sx={{
                                    borderRadius: "8px",
                                    background: darkMode ? "rgba(255, 152, 0, 0.12)" : "#fff8e1",
                                    border: `1px solid ${darkMode ? "#ffa000" : "#ffe082"}`
                                }}
                            >
                                <MDBox>
                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: darkMode ? "#ffb74d" : "#e65100" }}>
                                        Honorários cobrados de Terceiro (ex: Embargos de Terceiro)?
                                    </MDTypography>
                                    <MDTypography variant="caption" display="block" color="text">
                                        Ao ativar, será criada uma ficha própria de terceiro vinculada ao devedor principal, contendo os dados específicos do terceiro.
                                    </MDTypography>
                                </MDBox>
                                <Switch 
                                    checked={addForm.isTerceiro || false} 
                                    onChange={(e) => setAddForm({ ...addForm, isTerceiro: e.target.checked })} 
                                    color="warning" 
                                />
                            </MDBox>
                        </Grid>

                        {addForm.isTerceiro && (
                            <>
                                <Grid item xs={12} pt={1}>
                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: darkMode ? "#ffb74d" : "#e65100" }}>
                                        Dados Cadastrais do Terceiro Embargante
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <MDBox mb={1}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Nome Completo do Terceiro *</MDTypography>
                                        <MDInput
                                            fullWidth
                                            size="small"
                                            value={addForm.nomeTerceiro || ""}
                                            onChange={(e) => setAddForm({ ...addForm, nomeTerceiro: e.target.value })}
                                            placeholder="Nome do terceiro embargante"
                                        />
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <MDBox mb={1}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>CPF / CNPJ do Terceiro</MDTypography>
                                        <MDInput
                                            fullWidth
                                            size="small"
                                            value={addForm.documentoTerceiro || ""}
                                            onChange={(e) => {
                                                const doc = e.target.value;
                                                setAddForm(prev => ({
                                                    ...prev,
                                                    documentoTerceiro: doc,
                                                    codigoClienteTerceiro: !codigoTerceiroDiferente ? doc : prev.codigoClienteTerceiro
                                                }));
                                            }}
                                            placeholder="000.000.000-00"
                                        />
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <MDBox mb={1}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ minHeight: "22px", mb: 0.5 }}>
                                            <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"}>Cód. Cliente Terceiro *</MDTypography>
                                            <Button
                                                size="small"
                                                variant={codigoTerceiroDiferente ? "contained" : "outlined"}
                                                color={codigoTerceiroDiferente ? "warning" : "info"}
                                                onClick={() => {
                                                    const next = !codigoTerceiroDiferente;
                                                    setCodigoTerceiroDiferente(next);
                                                    if (!next) {
                                                        setAddForm(prev => ({ ...prev, codigoClienteTerceiro: prev.documentoTerceiro || "" }));
                                                    }
                                                }}
                                                sx={{ minWidth: "auto", px: 0.8, py: 0.2, fontSize: "0.65rem", textTransform: "none", height: "20px", lineHeight: 1 }}
                                            >
                                                {codigoTerceiroDiferente ? "Diferente" : "Diferente?"}
                                            </Button>
                                        </Box>
                                        <MDInput
                                            fullWidth
                                            size="small"
                                            disabled={!codigoTerceiroDiferente}
                                            value={addForm.codigoClienteTerceiro || ""}
                                            onChange={(e) => setAddForm({ ...addForm, codigoClienteTerceiro: e.target.value })}
                                            placeholder={codigoTerceiroDiferente ? "Informe código único" : (addForm.documentoTerceiro || "Espelho CPF/CNPJ")}
                                        />
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDBox mb={1}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Telefone de Contato</MDTypography>
                                        <MDInput
                                            fullWidth
                                            size="small"
                                            value={addForm.telefoneTerceiro || ""}
                                            onChange={(e) => setAddForm({ ...addForm, telefoneTerceiro: maskPhoneWithDdd(e.target.value) })}
                                            placeholder="(00) 00000-0000"
                                        />
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDBox mb={1}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>E-mail de Contato</MDTypography>
                                        <MDInput
                                            fullWidth
                                            size="small"
                                            value={addForm.emailTerceiro || ""}
                                            onChange={(e) => setAddForm({ ...addForm, emailTerceiro: e.target.value })}
                                            placeholder="terceiro@exemplo.com"
                                        />
                                    </MDBox>
                                </Grid>
                            </>
                        )}

                        {/* Seção 1: Configuração da Ficha */}
                        <Grid item xs={12} pt={addForm.isTerceiro ? 1 : 0}>
                            <MDTypography variant="button" fontWeight="bold" color="info">
                                1. Configuração da Ficha de Honorários
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>
                                    Carteira * {isCarteiraLocked && <span style={{ color: "#ffa000", fontSize: "11px", marginLeft: "4px" }}>(Equipe)</span>}
                                </MDTypography>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    placeholder="Selecione..."
                                    value={carteirasOptions.find(c => c.id === addForm.carteiraId) || null}
                                    options={carteirasOptions}
                                    onChange={(val) => setAddForm({ ...addForm, carteiraId: val ? val.id : "" })}
                                    isDisabled={isCarteiraLocked}
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Status da Ficha</MDTypography>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome || option.ccNome}
                                    placeholder="Selecione..."
                                    value={statusOptions.find(s => String(s.id) === String(addForm.statusId)) || null}
                                    options={statusOptions}
                                    onChange={(val) => setAddForm({ ...addForm, statusId: val ? val.id : "" })}
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Responsável (Propriedade)</MDTypography>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    placeholder="Selecione..."
                                    value={usuariosOptions.find(u => u.id === addForm.usuarioId) || null}
                                    options={usuariosOptions}
                                    onChange={(val) => setAddForm({ ...addForm, usuarioId: val ? val.id : "" })}
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Marcação Especial</MDTypography>
                                <MDSelect
                                    isMulti={true}
                                    getOptionValue={(option) => option.cvId}
                                    getOptionLabel={(option) => option.ccNome}
                                    placeholder="Selecione..."
                                    value={marcacoesOptions.filter(m => addForm.marcacoesIds && addForm.marcacoesIds.includes(m.cvId))}
                                    options={marcacoesOptions}
                                    onChange={(selected) => setAddForm({ ...addForm, marcacoesIds: selected ? selected.map(s => s.cvId) : [] })}
                                />
                            </MDBox>
                        </Grid>

                        {/* Seção 2: Cadastro do Débito */}
                        <Grid item xs={12} pt={2}>
                            <MDTypography variant="button" fontWeight="bold" color="info">
                                2. Cadastro do Débito de Honorários
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "text"} display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Tipo de Honorário</MDTypography>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    value={tipoHonorarioOptions.find(t => t.id === addForm.tipoHonorario) || null}
                                    options={tipoHonorarioOptions}
                                    onChange={(val) => {
                                        const selectedTipo = val ? val.id : 5;
                                        const selectedObj = tipoHonorarioOptions.find(t => t.id === selectedTipo);
                                        setAddForm({ 
                                            ...addForm, 
                                            tipoHonorario: selectedTipo, 
                                            titulo: selectedObj ? selectedObj.nome : "Honorários" 
                                        });
                                    }}
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color="text" display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Valor do Débito (R$) *</MDTypography>
                                <MDInput
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={addForm.valorOriginal}
                                    onChange={(e) => setAddForm({ ...addForm, valorOriginal: e.target.value })}
                                    placeholder="0.00"
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color="text" display="block" sx={{ minHeight: "22px", mb: 0.5 }}>Vencimento</MDTypography>
                                <MDTextFieldCalendar
                                    type="date"
                                    value={addForm.vencimento}
                                    onChange={(e) => setAddForm({ ...addForm, vencimento: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    darkMode={darkMode}
                                />
                            </MDBox>
                        </Grid>
                        <Grid item xs={12}>
                            <MDBox mb={1}>
                                <MDTypography variant="caption" fontWeight="medium" color="text">Observação / Descrição</MDTypography>
                                <MDInput
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={addForm.descricao}
                                    onChange={(e) => setAddForm({ ...addForm, descricao: e.target.value })}
                                    placeholder="Observações adicionais..."
                                />
                            </MDBox>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : undefined }}>
                    <MDButton 
                        variant="outlined" 
                        color="secondary" 
                        onClick={() => setAddModalOpen(false)} 
                        disabled={isSaving}
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton 
                        variant="contained" 
                        color="info" 
                        onClick={handleSaveAdd} 
                        disabled={isSaving}
                        sx={{
                            backgroundColor: darkMode ? "#007bff !important" : undefined,
                            color: "#ffffff !important",
                            fontWeight: "bold"
                        }}
                    >
                        {isSaving ? <CircularProgress size={20} color="inherit" /> : "Salvar Honorário Avulso"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
        </MDBox>
    );
}
